import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Search, Send, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useRedEstado, useTaxonomy } from "@/lib/red";
import { ProgressBar } from "@/components/red/RedSearchButton";
import { ServiceSelect } from "@/components/red/ClassificationSheet";
import { ResultCard } from "@/components/red/ResultCard";

const Divider = ({ label, testid }) => <p data-testid={testid} className="font-mono text-[10px] tracking-[0.2em] uppercase c-muted pt-2">{label}</p>;

const Results = ({ d, tax, q }) => {
  const keyOf = (name) => d.servicios.find((s) => s.nombre === name)?.clave || tax?.servicios.find((s) => s.nombre === name)?.clave;
  if (!d.resultados.length) return (
    <div data-testid="buscar-empty" className="c-card p-5 space-y-3 text-center">
      <p className="text-sm">Nadie en tu red lo tiene todavía. Invita a más personas a CONECTA</p>
      <Link to="/conecta/invitaciones" data-testid="buscar-empty-invite" className="c-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm"><Send className="w-4 h-4" /> Mis invitaciones</Link>
    </div>
  );
  const out = [];
  let lastGroup = null;
  let lastCircle = null;
  d.resultados.forEach((r, i) => {
    if (r.grupo !== lastGroup) {
      if (r.grupo === "otras_ciudades") out.push(<Divider key={`g${i}`} label="En otras ciudades" testid="buscar-divider-otras" />);
      lastGroup = r.grupo;
      lastCircle = null;
    }
    if (r.circulo !== lastCircle) {
      out.push(<Divider key={`c${i}`} label={r.circulo === 1 ? "1er círculo" : "2do círculo"} testid={`buscar-divider-c${r.circulo}`} />);
      lastCircle = r.circulo;
    }
    out.push(<ResultCard key={`${r.dueno.id}-${r.grupo}`} r={r} servicio={keyOf(r.servicio)} ciudad={d.ciudad} necesidad={q} />);
  });
  return <div className="space-y-2.5" data-testid="buscar-results">{out}</div>;
};

export default function BuscarRedPage() {
  const [sp] = useSearchParams();
  const [estado] = useRedEstado();
  const tax = useTaxonomy();
  const [q, setQ] = useState(sp.get("q") || "");
  const [svc, setSvc] = useState("");
  const [city, setCity] = useState("");
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastQ, setLastQ] = useState("");

  const run = useCallback(async (payload) => {
    setBusy(true); setErr(null); setLastQ(payload.necesidad || "");
    try { setD((await api.post("/red/buscar", payload)).data); } catch (e) {
      setD(null);
      setErr(e.response?.status === 403 && e.response.data?.detail?.conteo !== undefined ? { lock: e.response.data.detail } : { msg: errMsg(e) });
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { if (sp.get("q")) run({ necesidad: sp.get("q") }); }, [sp, run]);

  if (estado && !estado.activado) return (
    <div data-testid="buscar-locked" className="c-card p-4 space-y-3"><p className="font-semibold">Buscar en la red CONECTA</p><ProgressBar conteo={estado.conteo} meta={estado.meta} /></div>
  );
  return (
    <div data-testid="buscar-red-page" className="space-y-3">
      <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) run({ necesidad: q.trim() }); }} className="c-card p-3 space-y-2">
        <p className="text-xs c-text-2">¿Qué necesitas? Tu red solo verá a quién le pides presentación.</p>
        <div className="flex gap-2">
          <input data-testid="buscar-input" className="c-input flex-1" placeholder="Ej. Necesito un notario en Monterrey" value={q} onChange={(e) => setQ(e.target.value)} />
          <button data-testid="buscar-submit" disabled={busy || !q.trim()} aria-label="Buscar" className="c-btn-primary px-4 flex items-center">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</button>
        </div>
        {tax && (
          <details className="text-sm">
            <summary data-testid="buscar-direct-toggle" className="cursor-pointer c-accent font-semibold text-xs">O elige el servicio</summary>
            <div className="pt-2 space-y-2">
              <ServiceSelect tax={tax} value={svc} onChange={setSvc} testid="buscar-service-select" />
              <input data-testid="buscar-city-input" className="c-input" placeholder="Ciudad (opcional)" value={city} onChange={(e) => setCity(e.target.value)} />
              <button type="button" data-testid="buscar-direct-submit" disabled={busy || !svc} onClick={() => run({ servicio: svc, ciudad: city.trim() || null })} className="c-btn-primary w-full py-2.5">Buscar este servicio</button>
            </div>
          </details>
        )}
      </form>
      {err?.lock && <div className="c-card p-4"><ProgressBar conteo={err.lock.conteo} meta={err.lock.meta} /></div>}
      {err?.msg && <p data-testid="buscar-error" className="c-card p-4 text-sm font-semibold">{err.msg}</p>}
      {d && !d.entendido && (
        <div data-testid="buscar-not-understood" className="c-card p-4 space-y-2">
          <p className="text-sm font-semibold">{d.mensaje}</p>
          {d.sugerencias.length > 0 && <p className="text-xs c-text-2">¿Quisiste decir…?</p>}
          <div className="flex flex-wrap gap-1.5">
            {d.sugerencias.map((s) => <button key={s.clave} data-testid="buscar-suggestion" onClick={() => run({ servicio: s.clave, ciudad: d.ciudad })} className="px-3 py-1.5 rounded-full border c-line text-xs font-semibold">{s.nombre}</button>)}
          </div>
        </div>
      )}
      {d?.entendido && (
        <>
          <p data-testid="buscar-interpretation" className="text-xs c-text-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 c-accent" /> {d.servicios.map((s) => s.nombre).join(", ")}{d.ciudad ? ` en ${d.ciudad}` : ""} · te quedan {d.busquedas_restantes} búsquedas hoy</p>
          <Results d={d} tax={tax} q={lastQ} />
        </>
      )}
    </div>
  );
}
